import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, verifyMFA } from '../../redux/actions/userActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import './Login.css';

const FRONTEND_LOGIN_ERROR_CODES = {
  LOGIN_INVALID_CREDENTIALS: ErrorCodes.AUTH_INVALID_CREDENTIALS,
  LOGIN_REQUEST_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  MFA_VERIFY_FAILED: ErrorCodes.API_INVALID_PARAMETER,
  MFA_VERIFY_EXCEPTION: ErrorCodes.API_INTERNAL_ERROR,
};

const buildLoginPageSupportError = (errorCode, pageInfo, sourceMessage) =>
  formatFrontendSupportError({
    errorCode,
    pageInfo,
    sourceMessage,
  });

const getLoginFailureErrorDetails = (rawMessage) => {
  const normalizedMessage = typeof rawMessage === 'string' ? rawMessage : '';
  const isInvalidCredentials =
    normalizedMessage.includes('Incorrect username/password') ||
    normalizedMessage.startsWith('1003 -');

  if (isInvalidCredentials) {
    return {
      errorCode: FRONTEND_LOGIN_ERROR_CODES.LOGIN_INVALID_CREDENTIALS,
      pageInfo: 'Login Page / Invalid Username or Password',
      sourceMessage: normalizedMessage,
    };
  }

  return {
    errorCode: FRONTEND_LOGIN_ERROR_CODES.LOGIN_REQUEST_FAILED,
    pageInfo: 'Login Page / Sign-In Failed',
    sourceMessage: normalizedMessage,
  };
};

const LoginScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userName, setUserName] = React.useState({ value: '', error: '' });
  const [password, setPassword] = React.useState({ value: '', error: '' });
  const [showMFAModal, setShowMFAModal] = React.useState(false);
  const [tempSession, setTempSession] = React.useState('');
  const [mfaError, setMfaError] = React.useState('');
  const [mfaVerifying, setMfaVerifying] = React.useState(false);
  const [mfaCode, setMfaCode] = React.useState('');
  const [supportErrorMessage, setSupportErrorMessage] = React.useState('');
  const [copiedSupportError, setCopiedSupportError] = React.useState(false);
  
  const mfaInputRef = React.useRef(null);

  const userLogin = useSelector(state => state.userLogin);

  const setLoginSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
    setSupportErrorMessage(buildLoginPageSupportError(errorCode, pageInfo, sourceMessage));
    setCopiedSupportError(false);
  }, []);

  const clearLoginSupportError = React.useCallback((force = false) => {
    if (!force) return;
    setSupportErrorMessage('');
    setCopiedSupportError(false);
  }, []);

  const handleCopySupportError = React.useCallback(async () => {
    if (!supportErrorMessage) return;

    try {
      await navigator.clipboard.writeText(supportErrorMessage);
      setCopiedSupportError(true);
      setTimeout(() => setCopiedSupportError(false), 2000);
    } catch (error) {
      setCopiedSupportError(false);
    }
  }, [supportErrorMessage]);

  React.useEffect(() => {
    if (userLogin && userLogin.userInfo) {
      // Handle MFA verification required (already enrolled)
      if (userLogin.userInfo.status === 'mfa_required') {
        setTempSession(userLogin.userInfo.temp_session);
        setShowMFAModal(true);
        clearLoginSupportError(true);
        return;
      }

      // Admin without MFA: forced enrolment. Hand the temp session to the setup
      // page, which walks through QR enrolment and completes login on success.
      if (userLogin.userInfo.status === 'mfa_setup_required') {
        clearLoginSupportError(true);
        navigate('/mfa-setup', { state: { temp_session: userLogin.userInfo.temp_session } });
        return;
      }

      // Handle successful login with session
      if (userLogin.userInfo.session && userLogin.userInfo.session.loggedin) {
        clearLoginSupportError(true);
        // A temp-password (or admin-reset) account must set its own password
        // before reaching the dashboard.
        if (userLogin.userInfo.session.must_change_password) {
          navigate('/change-password', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
        return;
      }

      // Handle legacy login errors if reducer payload shape changes
      if (userLogin.userInfo.status === false) {
        const { errorCode, pageInfo, sourceMessage } = getLoginFailureErrorDetails(userLogin.userInfo.msg);
        const formattedError = buildLoginPageSupportError(errorCode, pageInfo, sourceMessage);
        setLoginSupportError(errorCode, pageInfo, sourceMessage);
        setPassword(prevMobData => ({
          ...prevMobData,
          error: formattedError,
        }));
      }
    }

    // Handle login failures (USER_LOGIN_FAIL reducer path)
    if (userLogin && userLogin.error) {
      const rawLoginError =
        typeof userLogin.error === 'string'
          ? userLogin.error
          : userLogin.error?.msg || JSON.stringify(userLogin.error);
      const { errorCode, pageInfo, sourceMessage } = getLoginFailureErrorDetails(rawLoginError);
      const formattedError = buildLoginPageSupportError(errorCode, pageInfo, sourceMessage);
      setLoginSupportError(errorCode, pageInfo, sourceMessage);

      setPassword(prevMobData => ({
        ...prevMobData,
        error: formattedError,
      }));
    }

    if (userLogin && !userLogin.loading && !userLogin.error && !showMFAModal) {
      // Clear previous login error when login state settles without a failure.
      setPassword(prevMobData => ({ ...prevMobData, error: '' }));
      clearLoginSupportError(true);
    }
  }, [clearLoginSupportError, navigate, setLoginSupportError, showMFAModal, userLogin]);

  // Focus MFA input when MFA form appears
  React.useEffect(() => {
    if (showMFAModal && mfaInputRef.current) {
      setTimeout(() => {
        mfaInputRef.current?.focus();
      }, 100);
    }
  }, [showMFAModal]);

  const handleLogin = e => {
    e.preventDefault();
    if (userName.value === '' || userName.value.trim() === '') {
      setUserName({ ...userName, error: 'Please enter your username' });
    } else if (password.value === '' || password.value.trim() === '') {
      setPassword({ ...password, error: 'Please enter your password' });
    } else {
      const data = new FormData();
      data.append('username', userName.value);
      data.append('password', password.value);
      dispatch(login(data));
    }
  };

  const handleLoginKeyDown = e => {
    if (e.key === 'Enter') {
      handleLogin(e);
    }
  };

  const handleMFAVerify = async (code) => {
    setMfaVerifying(true);
    setMfaError('');
    
    try {
      const data = await dispatch(verifyMFA(code, tempSession));
      
      if (data && data.status) {
        setShowMFAModal(false);
        setMfaCode('');
        setTempSession('');
        
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 100);
      } else {
        const formattedError = buildLoginPageSupportError(
          FRONTEND_LOGIN_ERROR_CODES.MFA_VERIFY_FAILED,
          'Login Page / MFA Verification Failed',
          data?.msg || 'Invalid verification code'
        );
        setLoginSupportError(
          FRONTEND_LOGIN_ERROR_CODES.MFA_VERIFY_FAILED,
          'Login Page / MFA Verification Failed',
          data?.msg || 'Invalid verification code'
        );
        setMfaError(formattedError);
        setMfaVerifying(false);
      }
    } catch (err) {
      const rawError = err?.response?.data?.msg || err?.message || 'Unknown MFA verification error';
      const formattedError = buildLoginPageSupportError(
        FRONTEND_LOGIN_ERROR_CODES.MFA_VERIFY_EXCEPTION,
        'Login Page / MFA Verification Request Error',
        rawError
      );
      setLoginSupportError(
        FRONTEND_LOGIN_ERROR_CODES.MFA_VERIFY_EXCEPTION,
        'Login Page / MFA Verification Request Error',
        rawError
      );
      setMfaError(formattedError);
      setMfaVerifying(false);
    }
  };

  const handleMFACancel = () => {
    setShowMFAModal(false);
    setTempSession('');
    setMfaError('');
    setMfaCode('');
    setMfaVerifying(false);
  };


  return (
    <div className='loginContainer' style={{ backgroundImage: 'url(/assets/login-bg.jpg)' }}>
      <div className='blue_container' />
      <div className='login_form_container'>
        <div className='login_form_content'>
          <div className='login_form'>
            <h1 className='login_form_heading'>{showMFAModal ? 'Two-Factor Authentication' : 'Login to Watcher'}</h1>
            {supportErrorMessage && (
              <div className='support_error_banner'>
                <div className='support_error_banner_text'>
                  <strong>Support Error:</strong>
                  <code className='support_error_code'>{supportErrorMessage}</code>
                </div>
                <div className='support_error_actions'>
                  <button
                    type='button'
                    className='copy_support_error_button'
                    onClick={handleCopySupportError}
                  >
                    {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                  </button>
                  <button
                    type='button'
                    className='copy_support_error_button'
                    onClick={() => clearLoginSupportError(true)}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
            {!showMFAModal ? (
              <form onSubmit={handleLogin}>
                <TextInput
                  label='Username'
                  icon='user'
                  placeholder='Your Username'
                  value={userName.value}
                  onChange={e => {
                    setUserName({ value: e.target.value });
                    setPassword({ ...password, error: '' });
                  }}
                  onKeyDown={handleLoginKeyDown}
                  errorMessage={userName.error}
                />
                <TextInput
                  type='password'
                  label='Password'
                  icon='lock'
                  placeholder='Your Password'
                  value={password.value}
                  onChange={e => setPassword({ value: e.target.value })}
                  onKeyDown={handleLoginKeyDown}
                  errorMessage={password.error}
                />
                {userLogin && userLogin.loading ? (
                  <Button
                    style={{ alignSelf: 'flex-start' }}
                    disabled
                    aria-label='Logging in'
                  >
                    <span className='login_button_spinner' aria-hidden='true' />
                  </Button>
                ) : (
                  <Button type="submit" label='Login' style={{ alignSelf: 'flex-start' }} onClick={handleLogin} />
                )}
                <button
                  type='button'
                  className='forgot_password_link'
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot password?
                </button>
              </form>
            ) : (
              <div>
                <p style={{ marginBottom: '20px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Enter the 6-digit verification code from your authenticator app
                </p>
                <TextInput
                  ref={mfaInputRef}
                  label='Verification Code'
                  placeholder='000000'
                  value={mfaCode}
                  onChange={e => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setMfaCode(value);
                    setMfaError('');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (mfaCode.length === 6 && !mfaVerifying) {
                        handleMFAVerify(mfaCode);
                      }
                    }
                  }}
                  maxLength={6}
                  autoFocus
                  errorMessage={mfaError}
                  style={{ fontSize: '20px', letterSpacing: '8px', textAlign: 'center' }}
                />
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <Button
                    label='Cancel'
                    onClick={handleMFACancel}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-secondary)',
                      flex: 1,
                    }}
                  />
                  <Button
                    label={mfaVerifying ? 'Verifying...' : 'Verify'}
                    onClick={() => handleMFAVerify(mfaCode)}
                    disabled={mfaCode.length !== 6 || mfaVerifying}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className='logo_container'>
            <img src='/assets/logo-new.png' alt='Frontline Logo' />
          </div>
        </div>
        <p className='powered_container'>
          Powered By
          <span>
            Watcher
          </span>
        </p>
      </div>
    </div>
  );
};

export default LoginScreen;
